import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Search, UserMinus, AlertTriangle, Shield, CheckCircle, XCircle, History } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { format } from "date-fns";
import { getStorageUrl } from "@/lib/storage";

interface DeletionResult {
  success: boolean;
  tablesAffected: Record<string, number>;
  storageObjectsDeleted: number;
  anonUserId?: string;
  errors: string[];
}

export function UserDeletionManager() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [showNoNameProfiles, setShowNoNameProfiles] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isTestMode, setIsTestMode] = useState(false);
  const [scrubText, setScrubText] = useState(true);
  const [reason, setReason] = useState("");
  const [deletionResult, setDeletionResult] = useState<DeletionResult | null>(null);

  // Search users using RPC with email support and no-name filter
  const { data: searchResults, isLoading: searching } = useQuery({
    queryKey: ["user_search_deletion", searchQuery, showNoNameProfiles],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_search_users_for_deletion", {
        search_query: searchQuery || "",
        include_no_name: showNoNameProfiles,
      });

      if (error) {
        console.error("Search error:", error);
        return [];
      }

      return data || [];
    },
    enabled: searchQuery.length >= 2 || showNoNameProfiles,
  });

  // Fetch deletion audit log
  const { data: deletionLogs, isLoading: logsLoading } = useQuery({
    queryKey: ["deletion_audit_logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_deletion_audit")
        .select("*")
        .order("deleted_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await supabase.functions.invoke("delete-user-pii", {
        body: {
          targetUserId: selectedUser.id,
          mode: isTestMode ? "test" : "production",
          reason: reason.substring(0, 200),
          scrubText,
        },
      });

      if (response.error) throw response.error;
      return response.data as DeletionResult;
    },
    onSuccess: (data) => {
      setDeletionResult(data);
      queryClient.invalidateQueries({ queryKey: ["deletion_audit_logs"] });
      queryClient.invalidateQueries({ queryKey: ["admin_stats"] });
      
      if (data.success) {
        toast.success("User PII successfully deleted");
      } else {
        toast.warning("Deletion completed with some errors");
      }
    },
    onError: (error) => {
      toast.error(`Deletion failed: ${error.message}`);
    },
  });

  const handleDelete = () => {
    if (confirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    deleteUserMutation.mutate();
  };

  const resetForm = () => {
    setSelectedUser(null);
    setShowConfirmDialog(false);
    setConfirmText("");
    setIsTestMode(false);
    setScrubText(true);
    setReason("");
    setDeletionResult(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserMinus className="h-5 w-5 text-destructive" />
            Delete User (PII Purge)
          </CardTitle>
          <CardDescription>
            Permanently delete a user's personally identifiable information. This action cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="space-y-3">
            <Label>Search User</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showNoName"
                checked={showNoNameProfiles}
                onCheckedChange={(checked) => setShowNoNameProfiles(!!checked)}
              />
              <Label htmlFor="showNoName" className="text-sm text-muted-foreground cursor-pointer">
                Show profiles with no name
              </Label>
            </div>
          </div>

          {/* Search Results */}
          {searching && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          )}

          {searchResults && searchResults.length > 0 && !selectedUser && (
            <div className="border rounded-lg divide-y">
              {searchResults.map((user: any) => {
                const hasNoName = !user.full_name || user.full_name.trim() === "";
                const displayName = hasNoName ? (user.email || "Unknown User") : user.full_name;
                
                return (
                  <div
                    key={user.id}
                    className="p-3 flex items-center justify-between hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelectedUser(user)}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={getStorageUrl("avatars", user.avatar_url) || undefined} />
                        <AvatarFallback>{displayName[0]?.toUpperCase() || "?"}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{displayName}</p>
                          {hasNoName && (
                            <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                              No Name
                            </Badge>
                          )}
                          {user.is_verified === false && (
                            <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                              Unverified
                            </Badge>
                          )}
                        </div>
                        {!hasNoName && user.email && (
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline">{user.role || "No role"}</Badge>
                  </div>
                );
              })}
            </div>
          )}

          {/* Selected User */}
          {selectedUser && (
            <div className="space-y-4">
              {(() => {
                const hasNoName = !selectedUser.full_name || selectedUser.full_name.trim() === "";
                const displayName = hasNoName ? (selectedUser.email || "Unknown User") : selectedUser.full_name;
                
                return (
                  <>
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        You are about to delete all PII for: <strong>{displayName}</strong>
                        {selectedUser.email && !hasNoName && ` (${selectedUser.email})`}
                      </AlertDescription>
                    </Alert>

                    <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={getStorageUrl("avatars", selectedUser.avatar_url) || undefined} />
                        <AvatarFallback>{displayName[0]?.toUpperCase() || "?"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{displayName}</p>
                          {hasNoName && (
                            <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                              No Name
                            </Badge>
                          )}
                        </div>
                        {!hasNoName && selectedUser.email && (
                          <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Joined: {format(new Date(selectedUser.created_at), "PPP")}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
                        Change
                      </Button>
                    </div>
                  </>
                );
              })()}

              {/* Options */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="testMode"
                    checked={isTestMode}
                    onCheckedChange={(checked) => setIsTestMode(!!checked)}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label htmlFor="testMode" className="text-destructive font-medium">
                      Testing Wipe (Delete Everything)
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Deletes all data including records that would normally be anonymized. Use only in QA.
                    </p>
                  </div>
                </div>

                {!isTestMode && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="scrubText"
                      checked={scrubText}
                      onCheckedChange={(checked) => setScrubText(!!checked)}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="scrubText">Scrub User-Authored Text Fields</Label>
                      <p className="text-sm text-muted-foreground">
                        Replace discussion posts, messages, and other text with "[redacted]"
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason for Deletion (optional)</Label>
                  <Textarea
                    id="reason"
                    placeholder="Brief reason (max 200 chars, no PII)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={200}
                  />
                </div>

                <Button
                  variant="destructive"
                  onClick={() => setShowConfirmDialog(true)}
                  className="w-full"
                >
                  <UserMinus className="h-4 w-4 mr-2" />
                  Delete User PII
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deletion Audit Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Deletion Audit Log
          </CardTitle>
          <CardDescription>
            Recent user deletions (no PII stored)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : deletionLogs && deletionLogs.length > 0 ? (
            <ScrollArea className="h-[300px]">
              <div className="space-y-2">
                {deletionLogs.map((log) => (
                  <div key={log.id} className="p-3 border rounded-lg text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={log.deletion_type === "test" ? "secondary" : "outline"}>
                          {log.deletion_type}
                        </Badge>
                        <span className="font-mono text-xs text-muted-foreground">
                          {log.original_user_id.slice(0, 8)}...
                        </span>
                      </div>
                      <span className="text-muted-foreground">
                        {format(new Date(log.deleted_at), "PPp")}
                      </span>
                    </div>
                    {log.reason && (
                      <p className="text-muted-foreground mt-1">{log.reason}</p>
                    )}
                    <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Tables: {Object.keys(log.tables_affected || {}).length}</span>
                      <span>Storage: {log.storage_objects_deleted || 0} files</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <p className="text-center text-muted-foreground py-8">No deletions recorded yet</p>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Shield className="h-5 w-5" />
              Confirm PII Deletion
            </DialogTitle>
            <DialogDescription>
              This action is <strong>permanent and irreversible</strong>. All personally identifiable
              information for this user will be {isTestMode ? "completely deleted" : "deleted or anonymized"}.
            </DialogDescription>
          </DialogHeader>

          {isTestMode && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Testing Mode:</strong> ALL user data will be permanently deleted, including
                records that would normally be kept for reporting.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">
                {selectedUser?.full_name?.trim() || selectedUser?.email || "Unknown User"}
              </p>
              {selectedUser?.full_name?.trim() && selectedUser?.email && (
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Type DELETE to confirm</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={confirmText !== "DELETE" || deleteUserMutation.isPending}
            >
              {deleteUserMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Confirm Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={!!deletionResult} onOpenChange={() => resetForm()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {deletionResult?.success ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-yellow-500" />
              )}
              Deletion {deletionResult?.success ? "Complete" : "Completed with Errors"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {deletionResult?.anonUserId && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Anonymous ID assigned:</p>
                <p className="font-mono text-xs">{deletionResult.anonUserId}</p>
              </div>
            )}

            <div>
              <p className="font-medium mb-2">Tables Affected:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(deletionResult?.tablesAffected || {}).map(([table, count]) => (
                  <div key={table} className="flex justify-between">
                    <span className="text-muted-foreground">{table}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <span>Storage Objects Deleted:</span>
              <Badge>{deletionResult?.storageObjectsDeleted || 0}</Badge>
            </div>

            {deletionResult?.errors && deletionResult.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-1">Errors:</p>
                  <ul className="list-disc list-inside text-sm">
                    {deletionResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button onClick={resetForm}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}